<script lang="ts" setup>
import { computed, markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { FieldSchema } from '@FormModule';
import type { StripeCardModel } from '@ShoppingCartModule';
import type { Stripe, StripeCardElement, StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { useSessionStore } from '@Stores';

const props = defineProps<{
  creditCardNameRequired?: boolean;
  creditCardNumberRequired?: boolean;
  error?: string | boolean;
  field?: FieldSchema;
}>();

const { shelterProfile } = storeToRefs(useSessionStore());

// NOTE :: Load Stripe.js for the shelter's connected account. The PaymentIntent the api creates
// lives on that account. Stripe refuses a confirm that comes from the platform account.
const STRIPE_ERROR_KEY = 'components.forms.errors.stripeValidationError';

const cardComplete = ref(false);
const cardElement = shallowRef<StripeCardElement | null>(null);
const cardError = ref('');
const cardHolderName = ref('');
const stripe = shallowRef<Stripe | null>(null);
const model = defineModel<StripeCardModel>({
  default: {
    cardComplete: false,
    cardElement: null,
    cardHolderName: '',
    stripe: null,
    stripeError: '',
  },
});

const creditCardNameRequired = computed(
  () => props?.field?.settings?.creditCardNameRequired ?? props.creditCardNameRequired ?? true
);
const creditCardNumberRequired = computed(
  () => props?.field?.settings?.creditCardNumberRequired ?? props.creditCardNameRequired ?? true
);
const showCardError = computed(() => cardError.value || (!!props.error && !cardComplete.value));
const showCreditCardFee = computed(
  () => shelterProfile.value?.charge_adopter_cc_fee && Number(props.field?.settings?.cc_fee ?? 0) > 0
);
const connectedAccount = computed<string | undefined>(() => shelterProfile.value?.stripe_auth?.stripe_user_id);
const _model = computed<StripeCardModel>(() => ({
  cardComplete: cardComplete.value,
  cardElement: cardElement.value ? markRaw(cardElement.value) : null,
  cardHolderName: cardHolderName.value,
  stripe: stripe.value ? markRaw(stripe.value) : null,
  stripeError: cardError.value,
}));

onMounted(() => setupStripe());
onBeforeUnmount(() => cardElement.value?.destroy());

watch(_model, () => (model.value = _model.value));
watch(cardError, onCardErrorUpdate);
// NOTE :: The confirm step (`useStripeCardConfirm`) writes a decline onto the model from outside.
// Copy it into `cardError` so the message shows under the card and the field marks the error.
watch(
  () => model.value?.stripeError,
  (stripeError) => {
    const isExternalError = !!stripeError && stripeError !== cardError.value;
    if (isExternalError) cardError.value = stripeError;
  }
);

// HELPERS
async function setupStripe() {
  const { loadStripe } = await import('@stripe/stripe-js');

  const loadOptions = connectedAccount.value ? { stripeAccount: connectedAccount.value } : undefined;
  stripe.value = await loadStripe(import.meta.env.VITE_STRIPE_KEY, loadOptions);
  if (!stripe.value) return;

  const elements = stripe.value.elements();
  cardElement.value = elements.create('card', { style: { base: { fontSize: '14px' } } });

  cardElement.value.on('change', (event: StripeCardElementChangeEvent) => onCardChange(event));
  cardElement.value.mount('#card-element');
}

// EVENTS
function onCardErrorUpdate() {
  if (!props.field) return;

  if (cardError.value) {
    props.field.state.errors.add(STRIPE_ERROR_KEY);
    return;
  }

  props.field.state.errors.delete(STRIPE_ERROR_KEY);
}

// NOTE :: We no longer send the card to the api to get a token. Stripe tells us on the page
// whether the card fields are complete and valid. The real check (a decline, or a 3D Secure
// check) happens on confirm, after staff press submit.
function onCardChange(event: StripeCardElementChangeEvent) {
  cardComplete.value = event.complete;
  cardError.value = event.error?.message ?? '';
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="relative flex w-full flex-col">
      <AFieldLabel
        :error="error && !cardHolderName"
        :label="$t('common.name')"
        :required="creditCardNameRequired"
      />

      <AFieldInputText
        v-model="cardHolderName"
        :error="error && !cardHolderName"
        :placeholder="$t('cart.paymentMethod.validateName')"
        :required="creditCardNameRequired"
      />
    </div>

    <div class="relative flex w-full flex-col">
      <AFieldLabel
        :error="showCardError"
        :label="$t('cart.label.creditCard')"
        :required="creditCardNumberRequired"
      />

      <div
        id="card-element"
        :class="[
          'rounded-md border border-slate-300 px-3 py-2',
          showCardError ? '!placeholder:text-red-600 !border-red-600' : 'border-slate-300',
        ]"
      ></div>

      <p
        v-if="showCreditCardFee && field"
        class="text-sm text-gray-500"
      >
        {{ $t('cart.splitPayment.creditCardFee') }} ${{ field.settings?.cc_fee ?? 0 }}
      </p>

      <AFieldError
        class="!text-sm"
        :error="cardError"
      />
    </div>
  </div>
</template>
